import {
  Box,
  Heading,
  Text,
  VStack,
  Divider,
  Flex,
  Button,
  Grid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  RadioGroup,
  Radio,
  Stack,
  Icon,
  SimpleGrid,
  useToast,
  Badge,
  HStack,
  useColorModeValue,
  Card,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Assignment,
  Carrier,
  Maybe,
  Order,
  useGetOrderbyPkQuery,
  useUpdateOrderByPkMutation,
  useBrodcastOrderMutation,
  useUpdateAssignmentByOrderIdMutation,
  useGetCarrierByIdSubscription,
  useAssignOrderMutation,
  useGetAdminCarriersQuery,
} from '../utils/__generated__/graphql';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { icon, LatLngExpression } from 'leaflet';
import { ElementType, useCallback, useState } from 'react';
import { CancelModal, LoadingSpinner, ModernCard, OrderState, PageHeader } from './shared';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaArrowLeft,
  FaBroadcastTower,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaInfoCircle,
  FaShoppingBag,
  FaRupeeSign,
  FaTruck,
} from 'react-icons/fa';
import { useUserDefaultRole } from '@nhost/react';

// Define the props type for FitBounds component
interface FitBoundsProps {
  restaurantLocation: [number, number] | null;
  dropoffLocation: [number, number] | null;
}

const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();

  const navigate = useNavigate();
  const userRole = useUserDefaultRole();

  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // New state for assignment
  const [assignmentType, setAssignmentType] = useState<'RESTAURANT' | 'ADMIN'>('RESTAURANT');

  const {
    data,
    loading,
    error,
    refetch: refetch,
  } = useGetOrderbyPkQuery({
    variables: { id: orderId },
  });

  const [updateOrderByPk, { loading: updateLoading }] = useUpdateOrderByPkMutation({
    onCompleted: () => refetch(),
  });

  const [broadcastOrder] = useBrodcastOrderMutation({
    onCompleted: () => refetch(),
  });

  const [insertOrderAssignment] = useAssignOrderMutation({
    onCompleted: () => refetch(),
    onError: (error) => {
      console.error('Error assigning order:', error);
    },
  });

  const toast = useToast();

  const order = data && (data?.order_by_pk as Order);

  console.log('order', JSON.stringify(order, null, 2));

  const {
    data: carrierData,
    loading: carrierLoading,
    error: carrierError,
  } = useGetAdminCarriersQuery({
    variables: {
      radius: 50 * 1000, // within 5 km
      from: {
        type: 'Point',
        coordinates: order?.basket?.restaurant?.location?.coordinates,
      },
    },
    skip: userRole !== 'admin' && order?.status !== OrderState.ASSIGNED,
  });

  const orderedDate = dayjs(order?.created_at).fromNow();
  const updatedDate = dayjs(order?.updated_at).fromNow();
  const restaurantLocation = order?.basket?.restaurant?.location?.coordinates;
  const dropoffLocation = order?.dropoffLocation?.coordinates;

  const openCancelModal = () => {
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelReason('');
  };

  const handleCancelModalSubmit = useCallback(async () => {
    try {
      await updateOrderByPk({
        variables: {
          pk_columns: { id: orderId },
          _set: { status: OrderState.CANCELED, cancelReason: cancelReason },
        },
      });
      closeCancelModal();
    } catch (error: any) {
      console.error('Error canceling order:', error?.message);
    }
  }, [updateOrderByPk, orderId, cancelReason, closeCancelModal]);

  const getRestaurantCarrierId = () => {
    const carriers = order?.basket?.restaurant?.carriers;

    console.log('carriers', carriers);

    if (!carriers || !Array.isArray(carriers) || carriers.length === 0) {
      return undefined;
    }
    return (carriers as unknown as Carrier[])[0]?.id;
  };

  const handleassignOrderAssignment = useCallback(
    async (orderStatus: OrderState) => {
      try {
        const carrierId = getRestaurantCarrierId();

        const { data: updateOrderData } = await updateOrderByPk({
          variables: {
            pk_columns: { id: orderId },
            _set: { deliveredBy: assignmentType, status: 'ASSIGNED' },
          },
        });
        if (assignmentType === 'RESTAURANT' && updateOrderData?.update_order_by_pk?.id) {
          if (!carrierId) {
            throw new Error('No carrier available for restaurant');
          }
          await insertOrderAssignment({
            variables: {
              orderId,
              carrierId,
              status: orderStatus,
            },
          });
        }
      } catch (error: any) {
        console.error('Error assigning order:', error.message);
      }
    },
    [orderId, assignmentType, updateOrderByPk, insertOrderAssignment, getRestaurantCarrierId, order]
  );

  const handleAcceptOrder = useCallback(
    async (orderStatus: OrderState) => {
      try {
        await updateOrderByPk({
          variables: {
            pk_columns: { id: orderId },
            _set: { status: orderStatus },
          },
        });
      } catch (error: any) {
        console.error('Error accepting order:', error?.message);
      }
    },
    [updateOrderByPk, orderId]
  );

  const handleBrodcastOrderToCarriers = async () => {
    try {
      await broadcastOrder({
        variables: { orderId },
      });
      toast({
        title: 'Order assigned',
        description: 'The order has been successfully broadcast to the carriers.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Assignment failed',
        description: 'There was an error assigning the order. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading || carrierLoading) {
    return <LoadingSpinner text="Loading order details..." />;
  }

  if (error || carrierError) {
    console.error('Error fetching order details:', error, carrierError);
    toast({
      title: 'Error',
      description: 'Failed to load order details. Please try again later.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Order Details
          </Text>
          <Text color="gray.600">Please try again later or contact support.</Text>
        </VStack>
      </ModernCard>
    );
  }

  console.log('carrierData?.carrier', carrierData?.carrier, order);

  return (
    <>
      <PageHeader
        title={`Order #${orderId?.slice(-6).toUpperCase()}`}
        subtitle={`Created ${orderedDate}`}
        actions={
          <Button
            leftIcon={<Icon as={FaArrowLeft as ElementType} boxSize={5} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size="md"
            fontWeight="medium"
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Orders</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Back</Text>
          </Button>
        }
      />

      {/* Main Content Grid */}
      <Grid templateColumns={{ base: '1fr', xl: '1fr 400px' }} gap={6} maxW="full">
        {/* Left Column - Order Details & Map */}
        <VStack spacing={6} align="stretch">
          {/* Order Details Card */}
          <Card p={6} shadow="md" borderRadius="lg">
            <VStack spacing={6} align="stretch">
              <OrderInfo order={order as Order} orderedDate={orderedDate} updatedDate={updatedDate} />
              <Divider borderColor="gray.200" />
              <Box>
                <Heading as="h4" size="lg" mb={6} color="gray.900" letterSpacing="tight" fontWeight="700">
                  Order Items
                </Heading>
                <OrderItems items={(order as Order)?.basket?.basket_dishes} />
              </Box>
              <Divider borderColor="gray.200" />
              <OrderSummary order={order as Order} />
            </VStack>
          </Card>

          {/* Map Card */}
          <Card p={6} shadow="md" borderRadius="lg">
            <Heading as="h4" size="md" mb={4} color="gray.900" fontWeight="600">
              Delivery Route
            </Heading>
            <Box borderRadius="md" overflow="hidden" border="1px" borderColor="gray.200">
              <OrderDetailMap
                restaurantLocation={restaurantLocation}
                dropoffLocation={dropoffLocation}
                carriersData={
                  order?.status === OrderState.ASSIGNED && order?.assignment?.carrier && userRole === 'admin'
                    ? (carrierData?.carrier as Carrier[])
                    : null
                }
                carrierId={getRestaurantCarrierId()}
              />
            </Box>
          </Card>
        </VStack>

        {/* Right Column - Status & Actions */}
        <VStack spacing={6} align="stretch">
          {/* Status Card */}
          <Card p={6} shadow="md" borderRadius="lg">
            <Heading as="h4" size="md" mb={4} color="gray.900" fontWeight="600">
              Order Status
            </Heading>
            <OrderAssignment
              assignmentType={assignmentType}
              setAssignmentType={setAssignmentType}
              handleAssign={handleassignOrderAssignment}
              orderStatus={order?.status}
              deliveredBy={order?.deliveredBy}
              openCancelModal={openCancelModal}
              handleAcceptOrder={handleAcceptOrder}
              assignmentData={order?.assignment}
              cancelReason={order?.cancelReason}
              isCarrierExist={Boolean(getRestaurantCarrierId())}
              carrierData={order?.basket?.restaurant?.carriers?.[0] as Carrier}
              userRole={userRole}
            />
          </Card>

          {/* Carrier Assignment Section */}
          {order?.status === OrderState.ASSIGNED && (
            <Card p={6} shadow="md" borderRadius="lg">
              {order?.assignment ? (
                order?.assignment?.status === 'PENDING' ? (
                  <CarrierList
                    orderId={orderId as string}
                    refetch={refetch}
                    carriersData={carrierData?.carrier as Carrier[]}
                  />
                ) : (
                  <Box p={4} bg="blue.50" borderRadius="lg" _dark={{ bg: 'blue.900' }} textAlign="center">
                    <Text fontWeight="600" fontSize="md" color="blue.600" _dark={{ color: 'blue.300' }}>
                      🚚 Carrier is on the way to restaurant
                    </Text>
                  </Box>
                )
              ) : (
                <Button
                  w="full"
                  colorScheme="brand"
                  size="lg"
                  onClick={handleBrodcastOrderToCarriers}
                  leftIcon={<Icon as={FaBroadcastTower as ElementType} boxSize={5} />}
                  fontWeight="600"
                  py={6}
                  borderRadius="xl"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                    bg: 'brand.600',
                  }}
                  _active={{ transform: 'translateY(0)' }}
                >
                  <Text display={{ base: 'none', sm: 'block' }}>Broadcast to Carriers</Text>
                  <Text display={{ base: 'block', sm: 'none' }}>Broadcast</Text>
                </Button>
              )}
            </Card>
          )}
        </VStack>
      </Grid>
      <CancelModal
        isOpen={isCancelModalOpen}
        onClose={closeCancelModal}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        handleCancelModalSubmit={handleCancelModalSubmit}
        loading={updateLoading}
      />
    </>
  );
};

interface OrderAssignmentProps {
  assignmentType: any;
  setAssignmentType: any;
  handleAssign: any;
  orderStatus: Maybe<string> | undefined;
  deliveredBy: Maybe<string> | undefined;
  openCancelModal: () => void;
  handleAcceptOrder: (orderStatus: OrderState) => void;
  assignmentData: Assignment | null | undefined;
  cancelReason: Maybe<string> | undefined;
  isCarrierExist: boolean;
  carrierData: Carrier | null | undefined;
  userRole: string | null | undefined;
}

const OrderAssignment = (props: OrderAssignmentProps) => {
  const {
    assignmentType,
    setAssignmentType,
    handleAssign,
    orderStatus,
    openCancelModal,
    handleAcceptOrder,
    assignmentData,
    cancelReason,
    isCarrierExist,
    carrierData,
    userRole,
  } = props;

  // Color mode values
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <VStack spacing={6} align="stretch">
      {orderStatus === OrderState.PENDING || orderStatus === OrderState.ACCEPTED ? (
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" fontWeight="600" color={textColor} textAlign="center">
            {orderStatus === OrderState.PENDING ? 'Order Pending' : 'Order Accepted'}
          </Text>
          <Flex gap={3} justify="center" direction={{ base: 'column', sm: 'row' }}>
            <Button
              size="md"
              colorScheme="red"
              variant="outline"
              leftIcon={<Icon as={FaTimesCircle as ElementType} boxSize={4} />}
              onClick={() => openCancelModal()}
              borderRadius="md"
              fontWeight="600"
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'md',
                bg: 'red.50',
              }}
            >
              Cancel Order
            </Button>
            <Button
              size="md"
              colorScheme="green"
              leftIcon={<Icon as={FaCheckCircle as ElementType} boxSize={4} />}
              onClick={() => handleAcceptOrder(OrderState.COOKING)}
              borderRadius="md"
              fontWeight="600"
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: 'md',
                bg: 'green.600',
              }}
            >
              Start Cooking
            </Button>
          </Flex>
        </VStack>
      ) : null}

      {/* Show OrderAssignment only when status is Cooking */}
      {orderStatus === OrderState.COOKING && userRole === 'admin' ? (
        <Flex direction="column" justifyContent="space-between" mt={4} height="100%">
          <Box flexGrow={1}>
            <Heading as="h4" size="md" mb={4} color={textColor}>
              Assign Order
            </Heading>
            <RadioGroup onChange={(value) => setAssignmentType(value as 'RESTAURANT' | 'ADMIN')} value={assignmentType}>
              <Stack mt={4} spacing={3}>
                <Radio value="ADMIN" colorScheme="brand">
                  ADMIN
                </Radio>
                {isCarrierExist && (
                  <Radio value="RESTAURANT" colorScheme="brand">
                    RESTAURANT
                  </Radio>
                )}
              </Stack>
            </RadioGroup>
          </Box>

          <Button
            mt={4}
            colorScheme="brand"
            size="lg"
            isDisabled={!assignmentType}
            onClick={() => assignmentType === 'ADMIN' ? handleAssign(OrderState.PENDING) : handleAssign(OrderState.ASSIGNED)}
            borderRadius="lg"
            fontWeight="600"
          >
            Assign
          </Button>
        </Flex>
      ) : orderStatus === OrderState.COOKING && userRole === 'seller' ? (
        <Box>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3} p={4} bg="gray.50" borderRadius="lg">
              <Icon as={FaTruck as ElementType} color="blue.500" boxSize={5} />
              <Box>
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  {carrierData?.name}
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  {carrierData?.phone}
                </Text>
              </Box>
            </HStack>
            <Button
              colorScheme="brand"
              size="lg"
              width="100%"
              onClick={() => handleAssign(OrderState.ACCEPTED)}
              borderRadius="lg"
              fontWeight="600"
            >
              Assign
            </Button>
          </VStack>
        </Box>
      ) : null}

      {orderStatus === OrderState.ASSIGNED && (
        <Box>
          {assignmentData ? (
            <VStack spacing={4} align="stretch">
              <HStack spacing={3} p={4} bg="blue.50" borderRadius="lg">
                <Icon as={FaTruck as ElementType} color="blue.500" boxSize={5} />
                <Box>
                  <Text fontSize="md" fontWeight="semibold" color={textColor}>
                    {assignmentData?.carrier?.name}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {assignmentData?.carrier?.phone}
                  </Text>
                </Box>
              </HStack>
            </VStack>
          ) : null}
        </Box>
      )}

      {/* Show assigner name for Assigned, Shipping, Delivered, and Canceled statuses */}
      {orderStatus === OrderState.SHIPPING && (
        <Box p={4} bg="orange.50" borderRadius="lg" textAlign="center">
          <HStack justify="center" spacing={2}>
            <Icon as={FaTruck as ElementType} color="orange.500" boxSize={5} />
            <Text fontWeight="600" color="orange.700">
              On the way to Delivery Address
            </Text>
          </HStack>
        </Box>
      )}

      {(orderStatus === OrderState.COMPLETED || orderStatus === OrderState.DELIVERED) && (
        <Box p={4} bg="green.50" borderRadius="lg" _dark={{ bg: 'green.900' }} textAlign="center">
          <Text fontSize="lg" fontWeight="700" color="green.600" _dark={{ color: 'green.300' }}>
            🎉 Order Completed Successfully!
          </Text>
        </Box>
      )}

      {orderStatus === OrderState.CANCELED && (
        <Box p={4} bg="red.50" borderRadius="lg" _dark={{ bg: 'red.900' }}>
          <Text fontSize="md" fontWeight="700" color="red.600" _dark={{ color: 'red.300' }} mb={2}>
            Order Canceled
          </Text>
          {cancelReason && (
            <Text fontSize="sm" color="red.700" _dark={{ color: 'red.400' }}>
              Reason: {cancelReason}
            </Text>
          )}
        </Box>
      )}
    </VStack>
  );
};

const OrderInfo: React.FC<{ order: Order; orderedDate: string; updatedDate: string }> = ({
  order,
  updatedDate,
}) => {
  // Color mode values
  const textColor = useColorModeValue('gray.900', 'gray.100');

  return (
    <Box>
      <Heading as="h4" size="md" mb={4} color={textColor} fontWeight="600">
        Customer Information
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <VStack align="start" spacing={4}>
          <InfoItem label="Name" value={order.receiverName ?? 'N/A'} icon={FaUser} iconColor="blue.500" />
          <InfoItem label="Phone" value={order.phoneNumber ?? 'N/A'} icon={FaPhone} iconColor="green.500" />
          <InfoItem label="Address" value={order.direction ?? 'N/A'} icon={FaMapMarkerAlt} iconColor="red.500" />
        </VStack>
        <VStack align="start" spacing={4}>
          <InfoItem label="Status" value={order.status} isStatus icon={FaClock} iconColor="yellow.500" />
          <InfoItem label="Last Updated" value={updatedDate} icon={FaClock} iconColor="gray.500" />
          {order.cancelReason && (
            <InfoItem
              label="Cancel Reason"
              value={order.cancelReason}
              isAlert
              icon={FaInfoCircle}
              iconColor="red.500"
            />
          )}
        </VStack>
      </SimpleGrid>
    </Box>
  );
};

const InfoItem: React.FC<{
  label: string;
  value: string;
  isStatus?: boolean;
  isAlert?: boolean;
  icon?: any;
  iconColor?: string;
}> = ({ label, value, isStatus, isAlert, icon: Icon, iconColor }) => {
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <HStack spacing={3} align="start">
      {Icon && (
        <Box p={2} borderRadius="md" bg={`${iconColor?.split('.')[0]}.100`} color={iconColor} flexShrink={0}>
          <Icon as={Icon} boxSize={4} />
        </Box>
      )}
      <VStack align="start" spacing={1} flex={1}>
        <Text fontSize="xs" color={mutedColor} fontWeight="600" letterSpacing="wide" textTransform="uppercase">
          {label}
        </Text>
        {isStatus ? (
          <Badge
            colorScheme={
              value === 'COMPLETED'
                ? 'green'
                : value === 'CANCELED'
                  ? 'red'
                  : value === 'PENDING'
                    ? 'yellow'
                    : value === 'COOKING'
                      ? 'orange'
                      : value === 'SHIPPING'
                        ? 'blue'
                        : 'gray'
            }
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            textTransform="capitalize"
            fontWeight="600"
            letterSpacing="wide"
          >
            {value.toLowerCase()}
          </Badge>
        ) : isAlert ? (
          <Text color="red.500" fontSize="md" fontWeight="600">
            {value}
          </Text>
        ) : (
          <Text fontSize="md" color={textColor} fontWeight="600">
            {value}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

const OrderItems: React.FC<{ items: Order['basket']['basket_dishes'] }> = ({ items }) => {
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableRowBg = useColorModeValue('white', 'gray.800');
  const tableRowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead bg={tableHeaderBg}>
          <Tr>
            <Th fontSize="xs" textTransform="uppercase" py={3} color={mutedColor} fontWeight="600">
              Item
            </Th>
            <Th isNumeric fontSize="xs" textTransform="uppercase" py={3} color={mutedColor} fontWeight="600">
              Quantity
            </Th>
            <Th isNumeric fontSize="xs" textTransform="uppercase" py={3} color={mutedColor} fontWeight="600">
              Price
            </Th>
            <Th isNumeric fontSize="xs" textTransform="uppercase" py={3} color={mutedColor} fontWeight="600">
              Total
            </Th>
          </Tr>
        </Thead>
        <Tbody bg={tableRowBg}>
          {items?.map((basketDish, index) => (
            <Tr
              key={basketDish.id}
              bg={index % 2 === 0 ? 'white' : 'gray.50'}
              _dark={{ bg: index % 2 === 0 ? 'gray.800' : 'gray.700' }}
              _hover={{ bg: tableRowHoverBg }}
              transition="background 0.2s"
            >
              <Td fontWeight="600" color={textColor} py={3}>
                <HStack spacing={3}>
                  <Box p={2} borderRadius="md" bg="indigo.100" color="indigo.600" flexShrink={0}>
                    <Icon as={FaShoppingBag as ElementType} boxSize={4} />
                  </Box>
                  <Text>{basketDish.dish.name}</Text>
                </HStack>
              </Td>
              <Td isNumeric color={textColor} py={3}>
                <Badge colorScheme="brand" borderRadius="full" px={2} py={1} fontSize="xs" fontWeight="600">
                  {basketDish.quantity}
                </Badge>
              </Td>
              <Td isNumeric color={textColor} py={3}>
                ₹{Number(basketDish.dish.price).toFixed(2)}
              </Td>
              <Td isNumeric fontWeight="700" color={textColor} py={3}>
                ₹{(basketDish.quantity * Number(basketDish.dish.price)).toFixed(2)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const OrderSummary: React.FC<{ order: Order }> = ({ order }) => {
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <VStack spacing={4} align="stretch">
      <Heading as="h5" size="sm" color={textColor} fontWeight="600">
        Order Summary
      </Heading>
      <VStack spacing={3} align="stretch">
        <Flex justify="space-between" align="center" py={2}>
          <HStack spacing={2}>
            <Icon as={FaRupeeSign as ElementType} color="green.500" boxSize={4} />
            <Text color={mutedColor} fontSize="md" fontWeight="500">
              Subtotal:
            </Text>
          </HStack>
          <Text color={textColor} fontSize="md" fontWeight="600">
            ₹{order.subTotal.toFixed(2)}
          </Text>
        </Flex>

        <Flex justify="space-between" align="center" py={2}>
          <HStack spacing={2}>
            <Icon as={FaTruck as ElementType} color="blue.500" boxSize={4} />
            <Text color={mutedColor} fontSize="md" fontWeight="500">
              Delivery Charge:
            </Text>
          </HStack>
          <Text color={textColor} fontSize="md" fontWeight="600">
            ₹{order.deliveryCharge.toFixed(2)}
          </Text>
        </Flex>

        <Divider borderColor="gray.300" />

        <Flex justify="space-between" align="center" py={3}>
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Total Amount
          </Text>
          <Text color="brand.500" fontSize="xl" fontWeight="700">
            ₹{(order.subTotal + order.deliveryCharge).toFixed(2)}
          </Text>
        </Flex>
      </VStack>

      {order.note && (
        <Box mt={4} p={4} bg="yellow.50" borderRadius="lg" _dark={{ bg: 'yellow.900' }}>
          <HStack spacing={3}>
            <Icon as={FaInfoCircle as ElementType} color="yellow.600" boxSize={4} />
            <Box>
              <Text
                fontSize="xs"
                color="yellow.800"
                fontWeight="600"
                letterSpacing="wide"
                mb={2}
                textTransform="uppercase"
              >
                Special Instructions
              </Text>
              <Text fontSize="md" color="yellow.700" fontWeight="500">
                {order.note}
              </Text>
            </Box>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

export default OrderDetails;

interface OrderDetailMapProps extends FitBoundsProps {
  carriersData: Carrier[] | undefined | null;
  carrierId?: string | undefined | null;
}

const OrderDetailMap = (props: OrderDetailMapProps) => {
  const { restaurantLocation, dropoffLocation, carrierId } = props;

  if (!restaurantLocation || !dropoffLocation) return null;

  const { data } = useGetCarrierByIdSubscription({
    variables: {
      carrierId,
    },
  });

  const carrier = data?.carrier_by_pk;

  const hasRestaurantLocation = restaurantLocation && restaurantLocation.length === 2;
  const hasDropoffLocation = dropoffLocation && dropoffLocation.length === 2;

  const polylinePositions =
    hasRestaurantLocation && hasDropoffLocation
      ? [
        [restaurantLocation[1], restaurantLocation[0]],
        [dropoffLocation[1], dropoffLocation[0]],
      ]
      : null;

  return (
    <MapContainer
      center={
        hasRestaurantLocation
          ? {
            lat: restaurantLocation[1],
            lng: restaurantLocation[0],
          }
          : {
            lat: 0,
            lng: 0,
          }
      }
      zoom={17}
      scrollWheelZoom={false}
      style={{ height: '600px', width: '100%' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />

      {hasRestaurantLocation && (
        <Marker
          position={{
            lat: restaurantLocation[1],
            lng: restaurantLocation[0],
          }}
          icon={restaurantIcon}
        >
          <Popup>Restaurant</Popup>
        </Marker>
      )}

      {hasDropoffLocation && (
        <Marker
          position={{
            lat: dropoffLocation[1],
            lng: dropoffLocation[0],
          }}
          icon={customerIcon}
        >
          <Popup>Customer</Popup>
        </Marker>
      )}

      {/* {carriersData &&
        carriersData?.map(
          ({ id, name, status, location }) =>
            location && (
              <Marker
                key={id}
                position={{
                  lat: location?.coordinates?.[1],
                  lng: location?.coordinates?.[0],
                }}
                icon={carrierIcon}
              >
                <Popup>
                  Carrier {name} {status}
                </Popup>
              </Marker>
            )
        )} */}

      {carrier && (
        <Marker
          key={carrier?.id}
          position={{
            lat: carrier?.location?.coordinates?.[1],
            lng: carrier?.location?.coordinates?.[0],
          }}
          icon={carrierIcon}
        >
          <Popup>{carrier?.name}</Popup>
        </Marker>
      )}

      {polylinePositions && (
        <Polyline positions={polylinePositions as LatLngExpression[] | LatLngExpression[][]} color="teal" weight={3} />
      )}
    </MapContainer>
  );
};

interface CarrierListProps {
  orderId: string | undefined;
  refetch: () => void;
  carriersData: Carrier[] | undefined;
}

const CarrierList: React.FC<CarrierListProps> = ({ orderId, refetch, carriersData }) => {
  const [updateOrderAssignment] = useUpdateAssignmentByOrderIdMutation({
    onCompleted: () => refetch(),
  });
  const toast = useToast();

  const handleAssign = async (carrierId: string) => {
    try {
      await updateOrderAssignment({
        variables: {
          orderId,
          carrierId,
        },
      });
      toast({
        title: 'Order assigned',
        description: 'The order has been successfully assigned to the carrier.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Assignment failed',
        description: 'There was an error assigning the order. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <VStack spacing={6} align="stretch">
      <Heading as="h4" size="sm" color={textColor} fontWeight="600">
        {carriersData && carriersData?.length >= 1 ? 'Available Carriers' : 'No Delivery Person Available'}
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {carriersData?.map((carrier) => (
          <Box
            key={carrier.id}
            p={4}
            bg="white"
            borderRadius="md"
            border="1px"
            borderColor="gray.200"
            _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
            _hover={{
              transform: 'translateY(-1px)',
              shadow: 'md',
              borderColor: 'blue.500',
            }}
            transition="all 0.2s"
          >
            <VStack spacing={3} align="stretch">
              <HStack spacing={3}>
                <Box p={2} borderRadius="md" bg="blue.100" color="blue.600" flexShrink={0}>
                  <Icon as={FaTruck as ElementType} boxSize={4} />
                </Box>
                <Box flex={1}>
                  <Text fontSize="md" fontWeight="600" color={textColor} mb={1}>
                    {carrier.name}
                  </Text>
                  <Text fontSize="sm" color={mutedColor} mb={2}>
                    {carrier.phone}
                  </Text>
                  <Badge
                    colorScheme={carrier.isOnline ? 'green' : 'gray'}
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="full"
                    fontWeight="600"
                  >
                    {carrier.isOnline ? 'Online' : 'Offline'}
                  </Badge>
                </Box>
              </HStack>
              <Button
                w="full"
                colorScheme="blue"
                size="md"
                onClick={() => handleAssign(carrier.id)}
                borderRadius="md"
                fontWeight="600"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md',
                  bg: 'brand.600',
                }}
                _active={{ transform: 'translateY(0)' }}
              >
                Assign Order
              </Button>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

// Define SVG for Restaurant Icon (Utensils)
const restaurantSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
  <path fill="#d00" d="M12 2C10.7 2 9.5 2.7 9 4c-.5 1.3-1.5 2.3-2.8 2.8C4.6 7.5 3 9.5 3 12c0 2.5 2.5 4.5 5.5 4.5h.5v2c0 .6.4 1 1 1s1-.4 1-1v-2h2v2c0 .6.4 1 1 1s1-.4 1-1v-2h.5C18.5 16 21 14 21 12c0-2.5-1.6-4.5-3.2-5.2C16.5 6.3 15.5 5.3 15 4c-.5-1.3-1.7-2-3-2zM7 12c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 3-3 3-3-1.3-3-3zM12 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
</svg>`;
const restaurantSvgUrl = `data:image/svg+xml;base64,${btoa(restaurantSvg)}`;
const restaurantIcon = icon({
  iconUrl: restaurantSvgUrl,
  iconSize: [32, 32],
  iconAnchor: [12, 32],
  popupAnchor: [0, -32],
});

// Define SVG for Customer Icon (User)
const customerSvg = `<svg width="256px" height="256px" viewBox="-2.4 -2.4 28.80 28.80" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#000000" stroke-width="0.696" transform="matrix(1, 0, 0, 1, 0, 0)rotate(0)">

<g id="SVGRepo_bgCarrier" stroke-width="0"/>

<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048"/>

<g id="SVGRepo_iconCarrier"> <path d="M12 10C14.2091 10 16 8.20914 16 6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6C8 8.20914 9.79086 10 12 10Z" fill="#095840"/> <path d="M2.72778 5.81803C2.62732 5.41619 2.22012 5.17186 1.81828 5.27233C1.41643 5.37279 1.17211 5.77999 1.27257 6.18184L1.65454 7.7097C2.3593 10.5287 4.49604 12.7495 7.25018 13.5787L7.25018 18.0519C7.25015 18.9504 7.25012 19.6996 7.33009 20.2944C7.41449 20.9222 7.60016 21.489 8.05563 21.9445C8.5111 22.3999 9.0779 22.5856 9.7057 22.67C10.3005 22.75 11.0497 22.75 11.9482 22.7499H12.0522C12.9507 22.75 13.6999 22.75 14.2947 22.67C14.9225 22.5856 15.4892 22.3999 15.9447 21.9445C16.4002 21.489 16.5859 20.9222 16.6703 20.2944C16.7502 19.6996 16.7502 18.9504 16.7502 18.052L16.7502 13.859C17.7313 14.1514 18.4808 15.0039 18.6058 16.067L19.2553 21.5876C19.3037 21.9989 19.6764 22.2932 20.0878 22.2448C20.4992 22.1964 20.7934 21.8237 20.745 21.4123L20.0956 15.8918C19.8512 13.815 18.0912 12.2499 16.0002 12.2499H8.0847C5.64125 11.6764 3.71957 9.78517 3.10975 7.3459L2.72778 5.81803Z" fill="#095840"/> </g>

</svg>`;
const customerSvgUrl = `data:image/svg+xml;base64,${btoa(customerSvg)}`;
const customerIcon = icon({
  iconUrl: customerSvgUrl,
  iconSize: [32, 32],
  iconAnchor: [12, 32],
  popupAnchor: [0, -32],
});

// Define SVG for Carrier Icon (User)
const carrierSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 504 504" xml:space="preserve" width="800" height="800" stroke="#000" stroke-width="5.544"><path style="fill:#c11a1a" d="m200 265.833 24.702-124.573L264 209.833l-3.448 16-4.759 24z"/><path style="fill:#8a8895" d="M408 249.833c-13.683 0-24.691 11.45-23.966 25.291.68 12.978 12.328 22.709 25.324 22.709H416a8 8 0 0 0 8-8v-32a8 8 0 0 0-8-8h-8z"/><path style="fill:#5c546a" d="M448 353.833c-30.928 0-56 25.072-56 56s25.072 56 56 56 56-25.072 56-56-25.072-56-56-56zm0 88c-17.673 0-32-14.327-32-32 0-17.673 14.327-32 32-32s32 14.327 32 32c0 17.673-14.327 32-32 32z"/><path style="fill:#8a8895" d="M448 417.833a7.97 7.97 0 0 1-5.656-2.344l-72-72c-3.125-3.125-3.125-8.188 0-11.313s8.188-3.125 11.313 0l72 72a7.998 7.998 0 0 1 0 11.313 7.975 7.975 0 0 1-5.657 2.344z"/><path style="fill:#c11a1a" d="M296 409.833c-26.51 0-48-21.49-48-48v-48H144v96h152z"/><path style="fill:#c11a1a" d="M307.422 417.833H184c-4.422 0-8-3.578-8-8s3.578-8 8-8h123.422c5.25 0 10.164-2.578 13.156-6.891l52.609-75.992a31.93 31.93 0 0 0 3.398-30.102l-19.992-49.992a7.97 7.97 0 0 0-7.43-5.023H328c-4.422 0-8-3.578-8-8s3.578-8 8-8h21.164c9.875 0 18.617 5.922 22.289 15.078l19.992 50a47.91 47.91 0 0 1-5.102 45.148l-52.609 75.992a32.02 32.02 0 0 1-26.312 13.782z"/><path style="fill:#7e5c62" d="M256 313.833H104c-8.837 0-16-7.163-16-16s7.163-16 16-16h152c8.837 0 16 7.163 16 16s-7.163 16-16 16z"/><path style="fill:#eeb258" d="M152 281.833H16c-8.837 0-16-7.163-16-16v-80c0-8.837 7.163-16 16-16h136c8.837 0 16 7.163 16 16v80c0 8.836-7.163 16-16 16z"/><path style="fill:#ff811a" d="M0 201.833h168v16H0z"/><path style="fill:#43af08" d="M368 417.833c-4.422 0-8-3.578-8-8 0-48.523 39.477-88 88-88 15.461 0 30.672 4.07 43.977 11.758 3.828 2.211 5.141 7.109 2.93 10.93-2.219 3.828-7.125 5.133-10.93 2.93-10.883-6.289-23.32-9.617-35.977-9.617-39.703 0-72 32.297-72 72a7.995 7.995 0 0 1-8 7.999zM328 225.833h-56a16.002 16.002 0 0 1-14.313-8.844l-32-64c-3.949-7.906-.746-17.516 7.156-21.469 7.898-3.953 17.508-.758 21.469 7.156l27.574 55.156H328c8.836 0 16 7.164 16 16s-7.164 16.001-16 16.001z"/><path style="fill:#5c546a" d="M203.173 249.833h110.124a32.024 32.024 0 0 1 26.156 13.57 32.027 32.027 0 0 1 3.977 29.195l-38.527 99.396c-2.473 6.313-8.512 10.172-14.902 10.172-1.941 0-3.914-.359-5.832-1.109-8.227-3.227-12.285-12.508-9.063-20.734l38.359-98.943-97.465.453c-8.836 0-16-7.164-16-16"/><path style="fill:#eeb258" d="M295.299 74.143c-1.492-2.594-4.117-4.125-7.336-3.984-59.297 3.078-59.297 7.195-59.297 14.672 0 4.172 2.367 29.117 2.844 34.078a7.957 7.957 0 0 0 2.664 5.227 39.975 39.975 0 0 0 26.492 10.031c22.055 0 40-17.945 40-40a39.954 39.954 0 0 0-5.367-20.024z"/><path style="fill:#7e5c62" d="M24 233.833a8 8 0 0 1-8-8v-24h16v24a8 8 0 0 1-8 8zM136 233.833a8 8 0 0 1-8-8v-24h16v24a8 8 0 0 1-8 8z"/><path style="fill:#5c546a" d="M160 409.833c0 17.673-14.327 32-32 32s-32-14.326-32-32H72c0 30.928 25.072 56 56 56s56-25.071 56-56h-24z"/><path style="fill:#43af08" d="M129.472 314.507c-45.692 5.925-82.676 39.423-93.868 83.254-2.566 10.049 5.226 19.812 15.597 19.812h157.226c5.809-11.671 8.644-25.084 7.201-39.318-4.227-41.699-44.592-69.137-86.156-63.748z"/><path style="fill:#c11a1a" d="M310.198 68.136c-12.288-23.369-39.148-35.318-65.691-27.66-18.92 5.459-33.78 21.237-38.281 40.407-3.404 14.494-1.233 28.991 6.165 41.581 1.383 2.354 4.023 3.703 6.753 3.703h23.547c4.04 0 7.785-2.12 9.863-5.585l18.31-30.527a8.006 8.006 0 0 1 6.866-3.888h21.547c4.352 0 8.297-2.234 10.555-5.969 2.241-3.719 2.382-8.226.366-12.062z"/></svg>';
const carrierSvgUrl = `data:image/svg+xml;base64,${btoa(carrierSvg)}`;
const carrierIcon = icon({
  iconUrl: carrierSvgUrl,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});
